import { CompanyInterface, LogoURLInterface } from 'client/Interfaces/CompanyInterface'
import { ExternalTaxRatesInterface } from 'client/Interfaces/ExternalVATRatesInterface'
import {
  getRegionFromCountry,
  getTaxRateByVatRateId,
  getVatRatesMapByRegion,
} from 'client/Other/functions/externalTaxRateFunctions'
import { l } from 'client/Other/Translation'
import { isEqual } from 'lodash'
import * as React from 'react'

import { deleteCompanyLogo, getCompanyLogoUrl, searchCompanies, uploadImageData } from '../../../api/company'
import { Placement } from '../../../Global/AnalyticsEvents'
import { Segment } from '../../../Global/Segment'
import { SegmentEvents } from '../../../Global/SegmentEvents'
import { CompanySearchResultInterface } from '../../../Interfaces/CompanySearchResultInterface'
import { getCountriesAsMap } from '../../../Other/Countries'
import { isValidEmailAddress } from '../../../Other/functions/validationFunctions'
import { convertPctFromLocale } from '../../../Other/functions/vatFunctions'
import { flattenObject } from '../../../Other/Object'
import useAccountingNavigation, {
  AccountingViews,
  HandleAccountingNavigation,
} from '../../AgerasAccounting/hooks/useAccountingNavigation'
import CheckBox from '../../common/CheckBox/CheckBox'
import ImageInput from '../../common/ImageInput/ImageInput'
import Input from '../../common/Input/Input'
import InputGroupHeader from '../../common/Input/InputGroupHeader'
import { SearchSelect } from '../../common/SearchSelect/SearchSelect'
import { SelectInput } from '../../common/SelectInput/SelectInput'
import { formatCompanySearchResult } from '../../CompanySearch/CompanySearchResultFormatter'
import CompanySearchResultRow, { companySearchSupportedCountries } from '../../CompanySearch/CompanySearchResultRow'

const OptionalFields = [
  'company.amtsgericht',
  'company.hrNr',
  'company.vat',
  'company.settings.vatPeriod',
  'company.taxPrepaymentRegistered',
  'company.rcs',
  'company.legalStatus',
  'contact.phone',
  'company.settings.vatPeriod',
]

interface CompanyInformationFormInterface {
  values: any
  schema: any
  tradeNameId: string
  vatOptions: number[]
  locale: string
  company: CompanyInterface
  externalVATRates: ExternalTaxRatesInterface
  isAgerasNextMigration?: boolean
  openVATModal: (onAddNew: (newVal) => void) => void
  onChange: (values, changedFields: string[], autoFilledFields) => void
  beforeLogoUpload: () => Promise<void>
  afterLogoUpload: () => Promise<void>
  openLogoModal: (
    logo: string,
    fileName: string,
    changeLogoField: (logoId: string, content: string) => void,
    uploadLogo: (image: Blob, fileName: string) => Promise<LogoURLInterface>,
  ) => void
}

interface CompanyInformationFormComponentInterface extends CompanyInformationFormInterface {
  handleAccountingNavigation: HandleAccountingNavigation
}

interface StateType {
  logoDataUrl: string
  imageUploadOngoing: boolean
  showAllFields: boolean
  formValues: any
  changedFields: any
  originalValues: any
  autoFilledFields: any
}

class CompanyInformationFormComponent extends React.Component<CompanyInformationFormComponentInterface, StateType> {
  constructor(props) {
    super(props)
    this.state = {
      showAllFields: true,
      formValues: this.getFormValues(),
      originalValues: this.getFormValues(),
      logoDataUrl: this.placeholderLogo,
      imageUploadOngoing: false,
      changedFields: {},
      autoFilledFields: [],
    }
  }

  public componentDidMount() {
    this.loadCompanyLogo()
  }

  private placeholderLogo = '/images/add_logo.svg'

  public componentDidUpdate(prevProps) {
    const prevValues = this.getTradenameValues(prevProps.tradeNameId, prevProps.values)
    const currentValues = this.getTradenameValues()
    if (prevValues.logo && currentValues.logo && prevValues.logo.id !== currentValues.logo.id) {
      this.loadCompanyLogo()
    }
    const formValues = this.getFormValues()
    const prevFormValues = this.getFormValues(prevProps)
    if (!isEqual(formValues, prevFormValues)) {
      this.setState({ formValues })
    }
  }

  public UNSAFE_componentWillReceiveProps() {
    // For next users: if no vat rate id in settings, update settings
    if (this.props.isAgerasNextMigration) {
      const formVATRateId = this.state.formValues['company.vatRateId']
      const taxRates = this.props.externalVATRates?.taxRates || []
      if (!formVATRateId && taxRates?.length > 0) {
        const taxRate = getTaxRateByVatRateId(this.props.externalVATRates, formVATRateId, undefined, this.props.locale)
        this.setState(
          {
            formValues: {
              ...this.state.formValues,
              'company.vatRateId': taxRate?.id,
            },
            changedFields: {
              ...this.state.changedFields,
              'company.vatRateId': true,
            },
          },
          this.handleOnChange,
        )
      }
    }
  }

  private async loadCompanyLogo() {
    if (this.isLogoAvailable(this.getTradenameValues())) {
      const result = await getCompanyLogoUrl(this.props.tradeNameId)
      const logo = result || this.placeholderLogo
      this.setState({ logoDataUrl: logo })
    } else {
      this.setState({ logoDataUrl: this.placeholderLogo })
    }
  }

  private isLogoAvailable(values) {
    return Boolean(values && values.logo && values.logo.id && values.logo.id !== 'false')
  }

  private isDefaultTradeName() {
    const { tradeNames } = this.props.values
    return tradeNames[0] && tradeNames[0]._id === this.props.tradeNameId
  }

  private async uploadImageData(imageBlob: Blob, fileName: string, tradeNameId: string) {
    try {
      const changedFieldsBefore = this.state.changedFields
      const formValuesBefore = this.state.formValues
      delete formValuesBefore['logo.id']

      // Start uploading indicator
      this.setState({ imageUploadOngoing: true })
      // Process before logo upload hook
      await this.props.beforeLogoUpload()
      // Upload new image to S3 bucket
      const response = await uploadImageData(imageBlob, fileName, tradeNameId)
      // Wait a few seconds to let the backend generate the URL
      await new Promise((resolve) => setTimeout(resolve, 2000))
      // Fetch logo URL
      const newUrl = response.url
      // Process after logo upload hook
      await this.props.afterLogoUpload()
      // Refresh state
      this.setState(
        {
          imageUploadOngoing: false,
          logoDataUrl: newUrl,
          changedFields: { ...changedFieldsBefore, 'logo.id': true, 'logo.content': true },
          formValues: {
            ...this.state.formValues,
            ...formValuesBefore,
          },
        },
        this.handleOnChange,
      )
      return response
    } catch (error) {
      console.error('Failed to upload company logo. Error: ', error)
      this.setState({ imageUploadOngoing: false }, this.handleOnChange)
    }
  }

  private flattenTradenameValues(tradeNameId, values = this.props.values) {
    const tradeName = this.getTradenameValues(tradeNameId, values)
    return flattenObject(tradeName)
  }

  private getFormValues({ values, tradeNameId, company } = this.props) {
    const companyValues = [
      'amtsgericht',
      'hrNr',
      'vat',
      'vatRateId',
      'rcs',
      'legalStatus',
      'isNotVATLiable',
      'taxPrepaymentRegistered',
      'eInvoiceAddress',
      'firmenbuchregister',
    ]
    return companyValues.reduce(
      (acc, name) => {
        return { ...acc, [`company.${name}`]: values[name] }
      },
      { ...this.flattenTradenameValues(tradeNameId, values), ...company },
    )
  }

  private getGeneralFields() {
    return this.props.schema.fields.find((f) => f.name === 'general').fields
  }

  private getTradeNameFieldsFor(title) {
    return this.props.schema.tradeNames.fields.find((f) => f.title === title).fields
  }

  private getVATOptionsMap() {
    const map = {}
    this.props.vatOptions.map((name) => {
      map[name] = convertPctFromLocale(name, this.props.locale)
    })
    return map
  }

  private getTradenameValues(tradeNameId = this.props.tradeNameId, allValues = this.props.values) {
    const valuesById = tradeNameId && allValues.tradeNames.find((t) => t._id === tradeNameId)
    const emptyData = {
      name: '',
      address: { streetAddress1: '', streetAddress2: '', pobox: '', city: '', www: '' },
      contact: { name: { first: '', last: '' }, email: '', mobilePhone: '', phone: '' },
    }
    return valuesById || emptyData
  }

  private shouldShowField = (f) => this.state.showAllFields || !OptionalFields.includes(f.name)

  private onInputValChange = (f, value) => {
    const { autoFilledFields } = this.state
    if (
      value !== this.state.formValues[f.name] &&
      Object.keys(this.state.autoFilledFields).find((af) => f.name === af)
    ) {
      delete autoFilledFields[f.name]
    }
    this.setState(
      {
        autoFilledFields,
        formValues: {
          ...this.state.formValues,
          [f.name]: value.slice(0, 100),
        },
        changedFields: { ...this.state.changedFields, [f.name]: true },
      },
      this.handleOnChange,
    )
  }

  private renderInput = (f, disabled?) => {
    const isEmail = f.name === 'contact.email' || f.name === 'company.email'
    const value = this.state.formValues[f.name]
    const isValid = !isEmail || typeof value !== 'string' || value === '' || isValidEmailAddress(value)
    return (
      this.shouldShowField(f) && (
        <Input
          className={Object.keys(this.state.autoFilledFields).find((af) => af === f.name) ? ['autoFilled'] : []}
          key={f.name}
          label={f.label ? l(f.label) : ' '}
          value={value || ''}
          automation={f.name}
          disabled={disabled}
          invalid={!isValid}
          onChange={(value) => this.onInputValChange(f, value)}
        />
      )
    )
  }

  private renderCountry = () => (
    <Input
      className={['countryField']}
      key={'country'}
      label={l('Country')}
      value={getCountriesAsMap()[this.props.company['company.country']]}
      automation={'country'}
      borderless={true}
      disabled={true}
    />
  )

  private onVATPercentageChange(value: string) {
    this.setState(
      {
        formValues: {
          ...this.state.formValues,
          'company.vatRateId': value,
        },
        changedFields: { ...this.state.changedFields, 'company.vatRateId': true },
      },
      this.handleOnChange,
    )
  }

  private renderGeneralFields() {
    const { locale, company } = this.props
    const showBillyVATRates = Boolean(this.props?.isAgerasNextMigration)
    const originTaxId = this.state.formValues['company.vatRateId']
    const taxRate = getTaxRateByVatRateId(this.props.externalVATRates, originTaxId, undefined, locale)
    const generalFields = this.getGeneralFields().map((f) => {
      const isNotVATLiableKey = 'company.isNotVATLiable'

      switch (f.name) {
        case 'company.vatNumber':
          return (
            <div key={f.name}>
              {this.renderInput(f, this.state.formValues[isNotVATLiableKey])}
              <CheckBox
                className="isNotVATLiable"
                automation="isNotVATLiable"
                value={this.state.formValues[isNotVATLiableKey]}
                label={l('isNotVATLiable')}
                key={isNotVATLiableKey}
                onChange={(value) => {
                  this.setState(
                    {
                      formValues: {
                        ...this.state.formValues,
                        [isNotVATLiableKey]: value,
                      },
                      changedFields: { ...this.state.changedFields, [isNotVATLiableKey]: true },
                    },
                    this.handleOnChange.bind(this),
                  )
                }}
              />
            </div>
          )
        case 'company.vatRateId':
          if (showBillyVATRates) {
            const region = getRegionFromCountry(company.country, locale)
            return (
              <SelectInput
                key={f.name}
                value={taxRate.id}
                automation={'vatPct'}
                label={l('DefaultVAT')}
                hasTitles={true}
                searchPlaceholder={l('search.Default')}
                map={getVatRatesMapByRegion(this.props.externalVATRates, region, locale)}
                onChange={(value) => this.onVATPercentageChange(value)}
                addNewLabel={l('input.select.EditValues')}
                onAddNew={() => {
                  Segment.track(SegmentEvents.company.addVatRate)
                  this.props.handleAccountingNavigation(AccountingViews.SettingsVatRates)
                }}
                invalid={!taxRate.id}
              />
            )
          }
          break
        case 'company.vat':
          if (!showBillyVATRates) {
            return (
              !f.hidden &&
              this.shouldShowField(f) && (
                <SelectInput
                  key={f.name}
                  value={this.state.formValues[f.name] || 0}
                  automation={'vatPct'}
                  label={l(f.label)}
                  searchPlaceholder={l('search.Default')}
                  map={this.getVATOptionsMap()}
                  onChange={(value) => {
                    this.setState(
                      {
                        formValues: {
                          ...this.state.formValues,
                          [f.name]: Number(value),
                        },
                        changedFields: { ...this.state.changedFields, [f.name]: true },
                      },
                      this.handleOnChange,
                    )
                  }}
                  addNewLabel={l('input.select.EditValues')}
                  onAddNew={() => {
                    this.props.openVATModal((value) => {
                      this.setState(
                        {
                          formValues: {
                            ...this.state.formValues,
                            [f.name]: Number(value),
                          },
                          changedFields: { ...this.state.changedFields, [f.name]: true },
                        },
                        this.handleOnChange,
                      )
                    })
                  }}
                />
              )
            )
          }
          break
        case 'company.taxPrepaymentRegistered':
          return (
            !f.hidden &&
            this.shouldShowField(f) && (
              <CheckBox
                value={this.state.formValues[f.name]}
                label={l(f.label)}
                key={f.name}
                onChange={(value) => {
                  this.setState(
                    {
                      formValues: {
                        ...this.state.formValues,
                        [f.name]: value,
                      },
                      changedFields: { ...this.state.changedFields, [f.name]: true },
                    },
                    this.handleOnChange,
                  )
                }}
              />
            )
          )
        case 'company.settings.vatPeriod':
        case 'company.name':
          return null
        default:
          return this.renderInput(f)
      }
    })
    return generalFields.concat(this.renderCountry())
  }

  private renderFieldsForGroup(groupName, values) {
    return this.getTradeNameFieldsFor(groupName).map((f) => {
      return !f.hidden && this.renderInput(f)
    })
  }

  private handleOnChange() {
    this.props.onChange(this.state.formValues, Object.keys(this.state.changedFields), this.state.autoFilledFields)
  }

  private fillFieldsFromCompanySearch(result: CompanySearchResultInterface) {
    const data: { [key: string]: any } = { name: result.name }
    const autoFilled = {}
    // if we get more fields from the backend, add them here with the corresponding form fields
    // This kind of negates the point of using schema for the company fields but we have to map the values somehow
    const fields = {
      address1: 'address.streetAddress1',
      address2: 'address.streetAddress2',
      city: 'address.city',
      postcode: 'address.pobox',
      code: 'company.businessId',
    }
    Object.keys(fields).forEach((f) => {
      if (result[f]) {
        data[fields[f]] = result[f]
        autoFilled[fields[f]] = result[f]
      } else {
        data[fields[f]] = ''
      }
    })
    this.setState(
      {
        autoFilledFields: { ...autoFilled, 'company.name': result.name },
        formValues: {
          ...this.state.formValues,
          ...data,
          ...(this.isDefaultTradeName() ? { 'company.name': result.name } : {}),
          name: result.name,
        },
        changedFields: { ...this.state.changedFields, 'company.name': true, ...Object.keys(data) },
      },
      this.handleOnChange,
    )
  }

  private companyOnBlur(value) {
    const { autoFilledFields } = this.state
    if (value !== this.state.formValues['company.name']) {
      delete autoFilledFields['company.name']
      this.setState({
        changedFields: { ...this.state.changedFields, 'company.name': true },
      })
    }
    this.setState(
      {
        autoFilledFields,
        formValues: {
          ...this.state.formValues,
          ...(this.isDefaultTradeName() ? { 'company.name': value } : {}),
          name: value.slice(0, 100),
        },
      },
      this.handleOnChange,
    )
  }

  private renderCompanyNameField() {
    const country = this.props.company['company.country']
    if (companySearchSupportedCountries.includes(country)) {
      return (
        <SearchSelect
          className={this.state.autoFilledFields['company.name'] ? ['autoFilled'] : []}
          automation={'companyName'}
          value={this.state.formValues.name}
          label={l('CompanyName')}
          doSearch={async (searchTerm) => {
            const rows = await searchCompanies(country, searchTerm)
            return formatCompanySearchResult(rows)
          }}
          resultRowRenderer={(row, searchTerm) => <CompanySearchResultRow data={row} searchTerm={searchTerm} />}
          onSelect={(row) => this.fillFieldsFromCompanySearch(row)}
          onBlur={(value) => this.companyOnBlur(value)}
          onChange={(value) => this.companyOnBlur(value)}
          dataSource={{
            name: l(`companySearch.${country}.sourceName`),
            linkUrl: l(`companySearch.${country}.sourceUrl`),
            title: l('companySearch.informationBy'),
            logoFile: `/images/search_logo_${country}.png`,
          }}
          useCache={{
            maxResults: 50,
            searchableField: 'name',
          }}
          searchingMessage={l('companySearch.searchingForCompany')}
          placement={Placement.companySettingsModal}
        />
      )
    } else {
      return (
        <Input
          label={l('CompanyName')}
          value={this.state.formValues.name}
          automation={'CompanyName'}
          onChange={(value) => this.companyOnBlur(value)}
        />
      )
    }
  }

  public render() {
    const values = this.getTradenameValues(this.props.tradeNameId)
    return (
      <div className="companyInformationModal__form-wrapper">
        <ImageInput
          tradeNameId={this.props.tradeNameId}
          label={l('Logo')}
          logoDataUrl={this.state.logoDataUrl}
          allowDeletion={this.isLogoAvailable(values)}
          uploadImage={(blob, fileName) => this.uploadImageData(blob, fileName, this.props.tradeNameId)}
          loading={this.state.imageUploadOngoing}
          openLogoModal={this.props.openLogoModal}
          onChange={(logoId, content) => {
            this.setState(
              {
                formValues: {
                  ...this.state.formValues,
                  logo: { id: logoId, content },
                },
                changedFields: { ...this.state.changedFields, 'logo.id': true },
              },
              this.handleOnChange,
            )
          }}
          onDelete={() => {
            if (values.logo) {
              deleteCompanyLogo(this.props.tradeNameId).then(() => {
                this.setState(
                  {
                    formValues: {
                      ...this.state.formValues,
                      'logo.id': null,
                    },
                    logoDataUrl: this.placeholderLogo,
                    changedFields: { ...this.state.changedFields, 'logo.id': true },
                  },
                  this.handleOnChange,
                )
              })
            }
          }}
        />
        {this.renderCompanyNameField()}
        {this.isDefaultTradeName() && this.renderGeneralFields()}
        <InputGroupHeader title={l('Address')} />
        {this.renderFieldsForGroup('Address', values.address)}
        <InputGroupHeader title={l('Contact')} />
        {this.renderFieldsForGroup('Contact', values.contact)}
        <div className={'formLink'}>
          {!this.state.showAllFields && (
            <a onClick={() => this.setState({ showAllFields: true })}>{'Show all fields'}</a>
          )}
        </div>
      </div>
    )
  }
}

const CompanyInformationForm = (props: CompanyInformationFormInterface) => {
  const { handleAccountingNavigation } = useAccountingNavigation(!!props?.isAgerasNextMigration)
  return <CompanyInformationFormComponent {...props} handleAccountingNavigation={handleAccountingNavigation} />
}

export default CompanyInformationForm
